"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildAttributeMap = exports.asAttributeMap = exports.amplifyReporter = void 0;
const auth_1 = require("@aws-amplify/auth");
const analytics_1 = require("@aws-amplify/analytics");
const protocol_http_1 = require("@aws-sdk/protocol-http");
const logger_1 = require("../logger");
function amplifyReporter(info, config) {
    var _a;
    if (config.identityPoolId !== false) {
        auth_1.Auth.configure({
            region: config.region,
            identityPoolId: config.identityPoolId,
            userPoolId: config.userPoolId,
            userPoolWebClientId: config.userPoolWebClientId,
        });
    }
    const wrapAutoTrackMiddleware = (source) => {
        const { beforeAutoTrack } = config;
        return typeof beforeAutoTrack === 'function' ? () => { var _a; return (_a = beforeAutoTrack(source)) !== null && _a !== void 0 ? _a : {}; } : undefined;
    };
    const allMetadata = asAttributeMap({
        appName: info.service,
        service: info.service,
        domain: window.location.host,
        environment: info.environment,
        version: info.version,
    });
    analytics_1.Analytics.configure({
        region: config.region,
        appId: config.analyticsAppId,
        endpoint: {
            attributes: allMetadata,
        },
        ...config.buffering,
    });
    // Session autotracking is enabled by default for backwards compatibility reasons, so we _must_
    // call this unconditionally to ensure we opt out of session tracking when `autoTrackSessions` isn't set
    // See: https://docs.amplify.aws/lib/analytics/autotrack/q/platform/js/#session-tracking
    analytics_1.Analytics.autoTrack('session', {
        enable: config.autoTrackSessions === true,
        attributes: wrapAutoTrackMiddleware('session'),
    });
    if (config.autoTrackPageViews) {
        analytics_1.Analytics.autoTrack('pageView', {
            enable: true,
            eventName: 'pageView',
            type: 'SPA',
            provider: 'AWSPinpoint',
            attributes: wrapAutoTrackMiddleware('pageView'),
        });
    }
    if (config.autoTrackEvents) {
        analytics_1.Analytics.autoTrack('event', {
            enable: true,
            selectorPrefix: (_a = config.selectorPrefix) !== null && _a !== void 0 ? _a : 'data-analytics-',
            attributes: wrapAutoTrackMiddleware('event'),
        });
    }
    if (config.proxyUrl) {
        installPinpointProxy(new URL(config.proxyUrl));
    }
    const reporter = {
        trackEvent: function (event) {
            analytics_1.Analytics.record({
                name: event.message,
                attributes: asAttributeMap({
                    ...event.metadata,
                    ...event.tags,
                }, false),
                metrics: event.metrics,
            });
        },
        addBreadcrumb: function (breadcrumb) {
            reporter.trackEvent({
                message: breadcrumb.message,
                metadata: {
                    category: breadcrumb.category,
                    ...breadcrumb.metadata,
                },
            });
        },
        addMetadata: function (metadata) {
            Object.assign(allMetadata, asAttributeMap(metadata, true));
            analytics_1.Analytics.updateEndpoint({
                attributes: allMetadata,
            }).catch(() => {
                // Swallow; see: https://crimsonhq.slack.com/archives/G4UN6Q4KF/p1648599302847539
            });
        },
        setUser: function (user) {
            var _a, _b;
            analytics_1.Analytics.updateEndpoint({
                userId: (_a = user === null || user === void 0 ? void 0 : user.id) !== null && _a !== void 0 ? _a : '',
                userAttributes: user
                    ? asAttributeMap({
                        id: user.id,
                        email: user.email,
                        name: (_b = user.name) !== null && _b !== void 0 ? _b : user.email,
                        username: user.username,
                    })
                    : {},
            }).catch(() => {
                // Swallow; see: https://crimsonhq.slack.com/archives/G4UN6Q4KF/p1648599302847539
            });
        },
        setRouteName: function (routeName) {
            reporter.addMetadata({ routeName });
        },
        setPageName: function (pageName) {
            reporter.addMetadata({ pageName });
        },
    };
    return reporter;
}
exports.amplifyReporter = amplifyReporter;
/**
 * Pinpoint has strict attribute name and value length limits
 */
function asAttributeMap(values, groupValues = true) {
    const attributeMap = buildAttributeMap(values, undefined, groupValues);
    const checkedEntries = Object.entries(attributeMap).map(([key, value]) => {
        var _a, _b;
        const truncatedKey = key.length > 50 ? `___${key.slice(-47)}` : key;
        const truncatedValue = Array.isArray(value)
            ? (_a = value === null || value === void 0 ? void 0 : value.map((val) => val.slice(0, 100))) !== null && _a !== void 0 ? _a : null
            : (_b = value === null || value === void 0 ? void 0 : value.slice(0, 100)) !== null && _b !== void 0 ? _b : null;
        return [truncatedKey, truncatedValue];
    });
    // Pinpoint only accepts 40 attributes
    if (checkedEntries.length > 40) {
        logger_1.logger.error(`Amplify only allows 40 attributes per event, truncating to 40 attributes`, {
            attributes: checkedEntries,
        });
        checkedEntries.length = 40;
    }
    return Object.fromEntries(checkedEntries);
}
exports.asAttributeMap = asAttributeMap;
/**
 * Pinpoint expects `endpoint.attributes` and `endpoint.userAttributes` to have
 * values which are string arrays. This function takes in an object and ensures
 * all of its values are of type `string[]` to appease Pinpoint.
 */
function buildAttributeMap(values, parentKey = undefined, groupValues = true) {
    const valuesWithStringArrays = {};
    Object.entries(values).forEach(([key, value]) => {
        const combinedKey = parentKey ? [parentKey, key].join('.') : key;
        if (!value) {
            valuesWithStringArrays[combinedKey] = null;
        }
        else if (groupValues && Array.isArray(value)) {
            valuesWithStringArrays[combinedKey] = value.map((element) => typeof element === 'string' ? element : JSON.stringify(element));
        }
        else if (typeof value === 'object') {
            const flattenedAttribute = buildAttributeMap(value, combinedKey, groupValues);
            Object.assign(valuesWithStringArrays, flattenedAttribute);
        }
        else {
            const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
            valuesWithStringArrays[combinedKey] = groupValues ? [stringValue] : stringValue;
        }
    });
    return valuesWithStringArrays;
}
exports.buildAttributeMap = buildAttributeMap;
function installPinpointProxy(proxyUrl) {
    // No public API for overriding where the Pinpoint client sends events to... 🤮
    // In theory you can pass in an `endpoint` to the Pinpoint client's constructor like any other AWS
    // client, but Amplify's analytics doesn't expose anything we can use to get an endpoint threaded
    // down to the Pinpoint client's constructor.
    //
    // The Pinpoint client _also_ isn't available synchronously because it is instantiated when events
    // get sent out, and then reconfigured whenever the API credentials change. We need to hook `_initClients`
    // to ensure that the Pinpoint client being used is always patched with our custom endpoint.
    const provider = analytics_1.Analytics.getPluggable('AWSPinpoint');
    if (!provider || typeof provider._initClients !== 'function') {
        logger_1.logger.error('Installation of the Pinpoint proxy failed. This likely means the internals of the @aws-amplify/analytics package have changed.');
        return;
    }
    const originalInitClients = provider._initClients;
    const requestMiddleware = (next) => async (args) => {
        const pinpointClient = provider.pinpointClient;
        if (pinpointClient && proxyUrl.pathname !== '/' && protocol_http_1.HttpRequest.isInstance(args.request)) {
            // Add proxyUrl.pathname to final request url if it was provided
            const shouldStripSlash = proxyUrl.pathname.endsWith('/');
            args.request.path = `${proxyUrl.pathname}${args.request.path.slice(shouldStripSlash ? 1 : 0)}`;
            // Wrap request body so the proxy has signing info
            if (typeof args.request.body === 'string') {
                const credentials = await pinpointClient.config.credentials();
                args.request.body = JSON.stringify({
                    credentials,
                    data: JSON.parse(args.request.body),
                });
            }
        }
        return next(args);
    };
    provider._initClients = async (credentials) => {
        const result = await originalInitClients.call(provider, credentials);
        const pinpointClient = provider.pinpointClient;
        if (pinpointClient) {
            pinpointClient.config.endpoint = () => Promise.resolve({
                hostname: proxyUrl.hostname,
                // Passing proxyUrl.pathname here doesn't work; it gets overridden
                path: '/',
                port: undefined,
                protocol: proxyUrl.protocol,
            });
            pinpointClient.middlewareStack.remove(requestMiddleware);
            pinpointClient.middlewareStack.add(requestMiddleware, { step: 'finalizeRequest' });
        }
        return result;
    };
}
//# sourceMappingURL=amplifyReporter.js.map